import { useMemo } from "react";

import { getStoredUserData } from "@/utils/getStoredUser";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, Calendar, DollarSign } from "lucide-react";
import { formatCurrency } from "@/utils/formatCurrency";
import { useRevenue } from "../hooks/useRevenue";
import Loading from "@/components/fragments/Loadin";

export default function RevenueReportBase() {
  const userData = getStoredUserData();
  const email = userData?.email || "";

  const { data, isLoading, error } = useRevenue({ email });

  // Ambil 30 data terakhir dan format untuk chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Ambil 30 data terakhir
    const last30 = data.slice(-30);

    return last30.map((item) => ({
      tanggal: new Date(item.tanggal).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
      }),
      fullDate: item.tanggal,
      total: parseFloat(item.total_penjualan || "0"),
      totalRupiah: formatCurrency(String(item.total_penjualan)),
    }));
  }, [data]);

  // Hitung total keseluruhan
  const totalRevenue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    return chartData.reduce((sum, item) => sum + item.total, 0);
  }, [chartData]);

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-lg">
          <p className="mb-1 font-semibold text-gray-800">
            {payload[0].payload.fullDate}
          </p>
          <p className="font-bold text-green-600">
            {payload[0].payload.totalRupiah}
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return <Loading fullScreen message="Memuat Data" size="large" />;
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
        <div className="max-w-md p-6 text-center bg-white rounded-lg shadow-lg">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-800">
            Gagal Memuat Data
          </h3>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "Terjadi kesalahan"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 bg-[#efecfa]/50 dark:bg-transparent md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="flex items-center gap-3 mb-2 text-3xl font-bold text-gray-800 dark:text-white">
            <TrendingUp className="w-8 h-8 text-blue-600 dark:text-white " />
            Laporan Pendapatan Bulanan
          </h1>
          <p className="text-gray-600 dark:text-white/70">
            Visualisasi pendapatan 30 hari terakhir
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <div className="p-6 bg-white dark:bg-[#1e1e1e] border-l-4 border-green-500 shadow-md rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="mb-1 text-sm text-gray-600 dark:text-white">
                  Total Pendapatan
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(String(totalRevenue))}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-[#1e1e1e] border-l-4 border-blue-500 shadow-md rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="mb-1 text-sm text-gray-600 dark:text-white">
                  Total Hari
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {chartData.length} hari
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-[#1e1e1e] border-l-4 border-purple-500 shadow-md rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="mb-1 text-sm text-gray-600 dark:text-white">
                  Rata-rata/Hari
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {chartData.length > 0
                    ? formatCurrency(String(totalRevenue / chartData.length))
                    : "Rp 0"}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Chart Container - LANDSCAPE OPTIMIZED */}
        <div className="p-6 bg-white dark:bg-[#1e1e1e] shadow-lg rounded-xl">
          <h2 className="mb-4 text-xl font-bold text-gray-800 dark:text-white">
            Grafik Pendapatan Harian
          </h2>

          {chartData.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg text-gray-500 dark:text-white">
                Tidak ada data untuk ditampilkan
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="tanggal"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                  tickFormatter={(value: number) => {
                    if (value >= 1000000)
                      return `${(value / 1000000).toFixed(1)}jt`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}rb`;
                    return value.toString();
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="rect" />
                <Bar
                  dataKey="total"
                  fill="#3b82f6"
                  name="Total Penjualan"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={50}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Data Table */}
        <div className="p-6 mt-6 bg-white dark:bg-[#1e1e1e] shadow-lg rounded-xl">
          <h2 className="mb-4 text-xl font-bold text-gray-800 dark:text-white">
            Detail Pendapatan
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="px-4 py-3 font-semibold text-left text-gray-700 dark:text-white">
                    No
                  </th>
                  <th className="px-4 py-3 font-semibold text-left text-gray-700 dark:text-white">
                    Tanggal
                  </th>
                  <th className="px-4 py-3 font-semibold text-right text-gray-700 dark:text-white">
                    Total Penjualan
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr
                    key={index}
                    className="transition-colors border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-gray-600 dark:text-white">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-white">
                      {item.fullDate}
                    </td>
                    <td className="px-4 py-3 font-semibold text-right text-green-600 dark:text-green-300">
                      {item.totalRupiah}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
